// src/client/hooks/useProfileNavigation.ts

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { ApiClient } from '@client/lib/api-client';
import { parseDeepLink } from '@client/utils/deep-link-helpers';
import { useProfilesQuery } from '@client/hooks/useProfilesQuery';
import type { Profile } from '@shared/types';

// ✅ Response Type Definition
interface ProfileSearchResponse {
  profiles: Profile[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startIndex: number;
    endIndex: number;
    pageSize: number;
  };
  searchCriteria?: any;
}

/**
 * Profile Navigation Hook
 * Loads filtered profiles and calculates prev/next navigation
 * Uses the same query as ProfileManager for consistency
 */
export function useProfileNavigation(profileId: number) {
  const [searchParams] = useSearchParams();
  
  // Parse filters from URL (same as ProfileManager)
  const filters = useMemo(() => 
    parseDeepLink(searchParams),
    [searchParams]
  );
  
  // Use the SAME query hook as ProfileManager
  // This ensures identical API call structure and response handling
  const { data, isLoading } = useProfilesQuery(
    filters,
    { page: 1, limit: 5000 },              // Load many profiles for navigation context
    { field: 'createdAt', order: 'desc' }, // Default sorting (could be customized)
    { 
      enabled: true,
      //staleTime: 5 * 60 * 1000  // 5 min cache
    }
  );
  
  // Extract profile IDs from response
  const profileIds = useMemo(() => 
    data?.profiles?.map((p) => p.id) || [],
    [data]
  );
  
  // Find current profile's position in filtered results
  const currentIndex = profileIds.indexOf(profileId);
  
  // Calculate prev/next
  const next = currentIndex > 0 ? profileIds[currentIndex - 1] : null;
  const previous = currentIndex < profileIds.length - 1 ? profileIds[currentIndex + 1] : null;
  
  return {
    // Navigation IDs
    previous,
    next,
    
    // Navigation state
    hasNext: currentIndex > 0,
    hasPrevious: currentIndex < profileIds.length - 1,
    
    // Position info
    position: currentIndex >= 0 ? currentIndex + 1 : 0,
    total: data?.pagination?.total || profileIds.length,
    
    // Loading state
    isLoading,
    
    // Debug info (optional, can remove in production)
    _debug: {
      profileIds: profileIds.length,
      currentIndex,
      filters
    }
  };
}